import React, { useState, useEffect, useCallback, useMemo } from "react";
import Modal from "react-modal";
import Select from "react-select";
import { connect } from "react-redux";
import LoadingIcon from "mdi-react/LoadingIcon";

import { shutterBsConstants } from "../../../constants";
import { shutterBsActions } from "../../../redux/actions";

const InvoiceModal = ({
  isModalOpen,
  setIsModalOpen,
  spinnerActions,
  selectedEventGroupId,
  setSelectedEventGroupId,
  getInvoice,
  getInvoiceList,
}) => {
  const [invoice, setInvoice] = useState(null);
  const [invoiceOptions, setInvoiceOptions] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Spinner lookup (memoized)
  const spinner = useCallback(
    (type) => spinnerActions.findIndex((f) => f === type),
    [spinnerActions]
  );

  // Fetch invoice list when group changes
  useEffect(() => {
    if (!selectedEventGroupId) return;

    getInvoiceList(selectedEventGroupId, (items) => {
      setInvoiceOptions(items);
      setSelectedInvoice(items.length > 0 ? items[0] : null);
    });
    // eslint-disable-next-line
  }, [selectedEventGroupId]);

  // Fetch invoice pdf when invoice selection changes
  useEffect(() => {
    if (!selectedInvoice || !selectedEventGroupId) return;

    getInvoice(selectedEventGroupId, selectedInvoice.id, (data) => {
      setInvoice(data);
    });
    // eslint-disable-next-line
  }, [selectedInvoice]);

  // Blob URL memoization and avoid decoding repeatedly
  const pdfBlobUrl = useMemo(() => {
    if (!invoice) return null;

    try {
      const decodedData = atob(invoice);
      const dataArray = new Uint8Array(decodedData.length);
      for (let i = 0; i < decodedData.length; i++) {
        dataArray[i] = decodedData.charCodeAt(i);
      }

      const blob = new Blob([dataArray], { type: "application/pdf" });
      return URL.createObjectURL(blob);
    } catch (e) {
      console.error("Failed to decode invoice PDF", e);
      return null;
    }
  }, [invoice]);

  // Cleanup blob URL
  useEffect(() => {
    return () => {
      if (pdfBlobUrl) URL.revokeObjectURL(pdfBlobUrl);
    };
  }, [pdfBlobUrl]);

  // Close Handler
  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedEventGroupId(null);
    setInvoice(null);
    setSelectedInvoice(null);
    setInvoiceOptions([]);
  };

  const isInvoiceLoaded = spinner(shutterBsConstants.GET_INVOICE);
  const isInvoiceListLoaded = spinner(shutterBsConstants.GET_INVOICE_LIST);

  return (
    <Modal
      isOpen={isModalOpen}
      onRequestClose={handleModalClose}
      className="reel-detail-con"
    >
      <div className="main-con">
        {!isInvoiceListLoaded ? (
          <div className="text-center mt-4">
            <LoadingIcon className="loading-spinner" />
          </div>
        ) : (
          <>
            {/* Select dropdown */}
            <div className="row w-100 m-0 mt-4">
              <div className="col-12 col-lg-6">
                <Select
                  options={invoiceOptions}
                  value={selectedInvoice}
                  id="select-invoice"
                  placeholder="select an invoice:"
                  onChange={setSelectedInvoice}
                  getOptionLabel={(option) => option.invoiceNumber ?? ""}
                  getOptionValue={(option) => option.id ?? ""}
                />
              </div>
            </div>
            {/* PDF viewer */}
            {isInvoiceLoaded ? (
              <div className="row w-100 m-0 mt-4">
                <div className="col-12">
                  {pdfBlobUrl ? (
                    <embed
                      style={{
                        height: "70vh",
                        width: "100%",
                        display: "flex",
                      }}
                      title="PDF Viewer"
                      src={pdfBlobUrl}
                      type="application/pdf"
                    />
                  ) : (
                    <div className="text-center mt-4">
                      <p>Unable to load PDF</p>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="text-center mt-4">
                <LoadingIcon className="loading-spinner" />
              </div>
            )}

            <button onClick={handleModalClose} className="modal-close">
              <img
                src="/images/simpleCrossWhite.svg"
                alt="crossIcon"
                className="modal-close-img"
              />
            </button>
          </>
        )}
      </div>
    </Modal>
  );
};

// Mapping the component's props to the reducer's state
const mapStateToProps = (state) => ({
  spinnerActions: state.system.spinnerActions,
});

// Mapping the component's props to the related actions
const mapDispatchToProps = (dispatch) => ({
  getInvoice: (eventGroupId, invoiceId, onSuccess) =>
    dispatch(shutterBsActions.getInvoice(eventGroupId, invoiceId, onSuccess)),
  getInvoiceList: (eventGroupId, onSuccess) =>
    dispatch(shutterBsActions.getInvoiceList(eventGroupId, onSuccess)),
});

// Mapping action and store the function via props
export default connect(mapStateToProps, mapDispatchToProps)(InvoiceModal);
